import { t, useLocale } from "@/i18n/locale";
import { Message } from "@/i18n/Message";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  ArrowLeft,
  ArrowRight,
  RotateCw,
  ExternalLink,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  Star,
  X,
} from "lucide-react";
import { useAppNavigationBridge } from "./useAppNavigationBridge";
import { activeAppFrame } from "./activeAppFrame";
import PublicTrialCountdown from "./PublicTrialCountdown";
import LaunchProgress from "./LaunchProgress";

export default function AppWindowFrame({
  title,
  appKey,
  icon,
  enlarged,
  onEnlarge,
  externalUrl,
  onOpenExternal,
  actions,
  children,
  navigation,
  summary,
  headerActions,
  titleDetails,
  trialInBar = true,
  favorite,
  onToggleFavorite,
  onSurfaceReady,
  onFrameRevealed,
  trialRemainingSeconds,
  onReload,
  canEnlarge = true,
  launching = false,
  launchComplete = false,
  onCancelLaunch,
  onClose,
}: {
  title: string;
  appKey?: string;
  icon?: ReactNode;
  enlarged: boolean;
  onEnlarge: () => void;
  externalUrl?: string | null;
  onOpenExternal?: () => void;
  actions?: ReactNode;
  children: ReactNode;
  navigation?: ReactNode;
  summary?: ReactNode;
  /** Wide screens keep the app's actions and the trial clock in the bar,
   *  beside its name, so nothing but the app sits below it. */
  headerActions?: ReactNode;
  /** Sits right after the name on wide screens: the details button and a
   *  one-line tagline. */
  titleDetails?: ReactNode;
  /** Wide screens show the trial clock in the bar; phones give it a row. */
  trialInBar?: boolean;
  favorite?: boolean;
  onToggleFavorite?: () => void;
  onSurfaceReady?: (url: string, bridge?: boolean) => void;
  /** The app has painted its first frame. This is earlier than
   *  `onSurfaceReady` (which waits for `load`), so the launch overlay can stop
   *  covering an app the visitor can already use. */
  onFrameRevealed?: (url: string) => void;
  trialRemainingSeconds?: number | null;
  onReload?: () => void;
  canEnlarge?: boolean;
  /** The surface is launching: show the 3px pseudo-progress bar right under
   *  the header. It is absolutely positioned, so showing it never shifts the
   *  app content. */
  launching?: boolean;
  /** The launch became operable: the bar reaches 100% and hides. */
  launchComplete?: boolean;
  /** Cancellation lives in the ⋮ menu, not in the loading surface. The caller
   *  supplies it only while a launch is actually in progress. */
  onCancelLaunch?: () => void;
  /** Leave the Preview and return to the Grid. Rendered as a visible close
   *  action (and wired to Escape) whenever the caller owns the window. */
  onClose?: () => void;
}) {
  useLocale();
  const windowRef = useRef<HTMLElement>(null);
  useEffect(() => {
    windowRef.current?.querySelectorAll("details[open]").forEach(menu => menu.removeAttribute("open"));
    setEditingUrl(false);
    setUrlError(null);
  }, [appKey]);
  const browser = useAppNavigationBridge(windowRef);
  useEffect(() => {
    if (browser.state) onSurfaceReady?.(browser.state.url, true);
  }, [browser.state, onSurfaceReady]);
  useEffect(() => {
    const root = windowRef.current;
    const loaded = (event: Event) => {
      if (event.target instanceof HTMLIFrameElement && event.target === activeAppFrame(root)) {
        onFrameRevealed?.(event.target.src);
        onSurfaceReady?.(event.target.src, false);
      }
    };
    // The bridge signals first paint; `load` stays as the fallback for apps
    // served without it (external pages, older bundles).
    const rendered = (event: MessageEvent) => {
      if (event.data?.type !== "ato.app-navigation.v1.rendered") return;
      const frame = activeAppFrame(root);
      if (!frame || event.source !== frame.contentWindow) return;
      try {
        if (event.origin !== new URL(frame.src).origin) return;
      } catch {
        return;
      }
      onFrameRevealed?.(frame.src);
    };
    root?.addEventListener("load", loaded, true);
    window.addEventListener("message", rendered);
    return () => {
      root?.removeEventListener("load", loaded, true);
      window.removeEventListener("message", rendered);
    };
  }, [onSurfaceReady, onFrameRevealed]);
  const [editingUrl, setEditingUrl] = useState(false);
  const [draftUrl, setDraftUrl] = useState("");
  const [urlError, setUrlError] = useState<string | null>(null);
  useEffect(() => {
    if (!onClose && !onCancelLaunch) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // A menu or a detail dialog owns its own Escape; never close the window
      // out from under it.
      if (windowRef.current?.querySelector("details[open]")) return;
      if (document.querySelector("dialog[open]")) return;
      if (editingUrl) return;
      event.preventDefault();
      // Escape leaves the launch before it leaves the surface: cancelling also
      // abandons the allocation, while closing only navigates away.
      if (onCancelLaunch) { onCancelLaunch(); return; }
      if (enlarged) onEnlarge();
      else onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onCancelLaunch, onClose, enlarged, onEnlarge, editingUrl]);
  useEffect(() => {
    setDraftUrl(browser.state?.url ?? "");
    setUrlError(null);
  }, [browser.state?.url]);
  useEffect(() => {
    const checkFrame = (event: MessageEvent) => {
      if (event.data?.type !== "ato:app-info:frame-check") return;
      const frame = activeAppFrame(windowRef.current);
      if (!frame || event.source !== frame.contentWindow) return;
      frame.contentWindow?.postMessage(
        { type: "ato:app-info:frame-result", embedded: true },
        event.origin === "null" ? "*" : event.origin,
      );
    };
    window.addEventListener("message", checkFrame);
    return () => window.removeEventListener("message", checkFrame);
  }, []);
  const shownAddress = editingUrl || urlError
    ? draftUrl
    : browser.state
      ? `${title}${new URL(browser.state.url).pathname === "/" ? "" : new URL(browser.state.url).pathname}${new URL(browser.state.url).hash}`
      : "";
  return (
    <section
      ref={windowRef}
      className={`discovery-window${enlarged ? " discovery-window--enlarged" : ""}`}
      aria-label={t("{{value0}} app window", { value0: title })}
    >
      <header className="discovery-window-bar">
        <LaunchProgress active={launching} complete={launchComplete} />
        <button
          className="discovery-desktop-history"
          type="button"
          title={t("Back within app")}
          aria-label={t("Back within app")}
          disabled={!browser.state?.canBack}
          onClick={() => browser.command("back")}
        >
          <ArrowLeft size={20} aria-hidden="true" />
        </button>
        <button
          className="discovery-desktop-history"
          type="button"
          title={t("Forward within app")}
          aria-label={t("Forward within app")}
          disabled={!browser.state?.canForward}
          onClick={() => browser.command("forward")}
        >
          <ArrowRight size={20} aria-hidden="true" />
        </button>
        <button
          className="discovery-desktop-history"
          type="button"
          title={t("Reload app")}
          aria-label={t("Reload app")}
          disabled={!browser.state && !onReload}
          onClick={() => onReload ? onReload() : browser.command("reload")}
        >
          <RotateCw size={20} aria-hidden="true" />
        </button>
        <form
          className="discovery-app-address"
          onSubmit={(event) => {
            event.preventDefault();
            if (!browser.command("navigate", draftUrl))
              setUrlError(t("Enter a URL within this app."));
          }}
        >
          {icon}
          <input
            type="text"
            aria-label={t("App URL")}
            aria-invalid={!!urlError}
            title={browser.state ? undefined : title}
            placeholder={title}
            value={shownAddress}
            size={Math.max(4, (shownAddress || title).length)}
            onFocus={() => {
              setEditingUrl(true);
              setDraftUrl(browser.state?.url ?? "");
            }}
            onBlur={() => setEditingUrl(false)}
            disabled={!browser.state}
            onChange={(event) => setDraftUrl(event.target.value)}
          />
        </form>
        {titleDetails}
        {trialInBar && trialRemainingSeconds != null ? <PublicTrialCountdown seconds={trialRemainingSeconds} /> : null}
        {headerActions}
        {onCancelLaunch ? (
          // The ⋮ menu alone is not discoverable while a slow admission runs.
          // Shown only when the caller says a launch is actually in progress.
          <button
            type="button"
            className="discovery-launch-cancel"
            onClick={onCancelLaunch}
          >
            {t("Cancel")}
          </button>
        ) : null}
        <button
          type="button"
          title={enlarged ? t("Restore app window") : t("Expand app window")}
          aria-label={enlarged ? t("Restore app window") : t("Enlarge app window")}
          aria-pressed={enlarged}
          className={enlarged ? "discovery-restore" : undefined}
          onClick={onEnlarge}
          disabled={!enlarged && !canEnlarge}
        >
          {enlarged ? <Minimize2 size={19} /> : <Maximize2 size={19} />}
          {enlarged ? <span>{t("Back to ato")}</span> : null}
        </button>
        <details className="discovery-window-more">
          <summary aria-label={t("More app actions")} title={t("More app actions")}>
            <MoreHorizontal size={20} aria-hidden="true" />
          </summary>
          <div className="discovery-window-menu">
            <div
              className="discovery-mobile-history"
              role="group"
              aria-label={t("App browsing history")}
            >
              <button
                type="button"
                disabled={!browser.state?.canBack}
                onClick={() => browser.command("back")}
              >
                <ArrowLeft size={18} aria-hidden="true" />
                <Message message={"Back within app"} /></button>
              <button
                type="button"
                disabled={!browser.state?.canForward}
                onClick={() => browser.command("forward")}
              >
                <ArrowRight size={18} aria-hidden="true" />
                <Message message={"Forward within app"} /></button>
              <button
                type="button"
                disabled={!browser.state && !onReload}
                onClick={() => onReload ? onReload() : browser.command("reload")}
              >
                <RotateCw size={18} aria-hidden="true" />
                <Message message={"Reload app"} /></button>
            </div>
            {onToggleFavorite ? (
              <button
                type="button"
                title={t("Add a shortcut to Favorites; separate from Install and Like")}
                onClick={(event) => {
                  onToggleFavorite();
                  event.currentTarget
                    .closest("details")
                    ?.removeAttribute("open");
                }}
                aria-pressed={favorite}
              >
                <Star
                  size={16}
                  fill={favorite ? "currentColor" : "none"}
                  aria-hidden="true"
                />
                {favorite ? t("Remove favorite") : t("Add to favorites")}
              </button>
            ) : null}
            {onCancelLaunch ? (
              <button
                type="button"
                onClick={(event) => {
                  event.currentTarget.closest("details")?.removeAttribute("open");
                  onCancelLaunch();
                }}
              >
                <X size={16} aria-hidden="true" />
                <Message message={"Cancel"} /></button>
            ) : null}
            {externalUrl ? (
              <a
                href={browser.state?.url ?? externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("Open app in new tab")}
                onClick={onOpenExternal}
              >
                <ExternalLink size={16} aria-hidden="true" />
                <Message message={"Open in new tab"} /></a>
            ) : null}
            {actions}
          </div>
        </details>
        {onClose ? (
          <button
            type="button"
            title={t("Close app window")}
            aria-label={t("Close app window")}
            onClick={onClose}
          >
            <X size={19} aria-hidden="true" />
          </button>
        ) : null}
      </header>
      {urlError ? (
        <p className="discovery-url-error" role="alert">
          {urlError}
        </p>
      ) : null}
      {!trialInBar && trialRemainingSeconds != null ? <div className="discovery-window-trial"><PublicTrialCountdown seconds={trialRemainingSeconds} /></div> : null}
      <div className="discovery-window-body">{children}</div>
      {summary}
      {navigation}
    </section>
  );
}
